import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Field, Input } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function PatientAllergyPage() {
  const { patientId = '' } = useParams<{ patientId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const returnTo = safeReturnTo(params.get('returnTo'), `/patients/${patientId}`);
  const [substance, setSubstance] = useState('');
  const [reaction, setReaction] = useState('');
  const [severity, setSeverity] = useState('moderate');

  const save = useMutation({
    mutationFn: () => post(`/patients/${patientId}/allergies`, { substance, reaction: reaction || undefined, severity }),
    onSuccess: () => {
      toast.success(tr('opd', 'allergy_recorded'), tr('opd', 'prescribing_will_now_warn_on_this_substance'));
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      navigate(returnTo, { replace: true });
    },
    onError: (error) => toast.error(tr('opd', 'could_not_record_the_allergy'), describeError(error)),
  });

  const ready = Boolean(substance.trim());
  const dirty = !save.isSuccess && (substance !== '' || reaction !== '' || severity !== 'moderate');

  return (
    <FormScreen
      title={tr('opd', 'record_an_allergy')}
      description={tr('opd', 'this_warns_every_prescriber_from_now_on')}
      icon={AlertTriangle}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={AlertTriangle}>
        <Field label={tr('opd', 'substance')} required hint={tr('opd', 'a_class_name_such_as_penicillin_also_catches')} className="sm:col-span-2">
          <Input value={substance} onChange={(e) => setSubstance(e.target.value)} />
        </Field>
        <Field label={tr('opd', 'reaction')}>
          <Input value={reaction} onChange={(e) => setReaction(e.target.value)} placeholder={tr('opd', 'rash_anaphylaxis')} />
        </Field>
        <Field label={tr('opd', 'severity')}>
          <ChoiceButtons
            name="severity"
            value={severity}
            onChange={setSeverity}
            options={[
              { value: 'mild', label: tr('opd', 'mild') },
              { value: 'moderate', label: tr('opd', 'moderate'), tone: 'warning' },
              { value: 'severe', label: tr('opd', 'severe'), tone: 'destructive' },
            ]}
          />
        </Field>
      </FormSection>

      <FormActions submitLabel={tr('opd', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
